// src/scenes/companies/CompanyPromoters.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
  CircularProgress,
  IconButton,
  Avatar,
  useTheme,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { Add, Edit, Delete } from "@mui/icons-material";
import { useParams } from "react-router-dom";
import axiosInstance from "../../axiosInstance";
import { tokens } from "../../theme";
import { toast } from "react-toastify";
import Header from "../../components/Header";

const initialForm = { name: "", post: "", imageFile: null };

const CompanyPromoters = () => {
  const { id: companyId } = useParams();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const [promoters, setPromoters] = useState([]);
  const [loading, setLoading] = useState(false);

  // Dialog states
  const [addEditOpen, setAddEditOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState(initialForm);
  const [deleteId, setDeleteId] = useState(null);

  /** ------------ FETCH ------------ */
  const fetchPromoters = () => {
    setLoading(true);
    axiosInstance
      .get(`/companies/${companyId}/promoters`)
      .then((res) => setPromoters(res.data || []))
      .catch(() => toast.error("Failed to load promoters"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchPromoters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  /** ------------ ADD / EDIT ------------ */
  const openAddEdit = (row = null) => {
    setEditing(row);
    setFormData(
      row ? { name: row.name, post: row.post, imageFile: null } : initialForm,
    );
    setAddEditOpen(true);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData((p) => ({ ...p, [name]: value }));
  };

  const handleFileChange = (file) => {
    setFormData((p) => ({ ...p, imageFile: file }));
  };

  const handleConfirmSave = () => {
    if (!formData.name.trim()) return toast.warn("Name is required");
    setConfirmOpen(true);
  };

  const submitAddEdit = () => {
    setLoading(true);
    const form = new FormData();
    form.append("name", formData.name);
    form.append("post", formData.post);
    if (formData.imageFile) form.append("imageFile", formData.imageFile);

    const request = editing
      ? axiosInstance.put(
          `/companies/${companyId}/promoters/${editing.id}`,
          form,
          {
            headers: { "Content-Type": "multipart/form-data" },
          },
        )
      : axiosInstance.post(`/companies/${companyId}/promoters`, form, {
          headers: { "Content-Type": "multipart/form-data" },
        });

    request
      .then(() => {
        toast.success(`Promoter ${editing ? "updated" : "added"} successfully`);
        fetchPromoters();
        setAddEditOpen(false);
      })
      .catch((err) => {
        const message =
          err.response?.data?.message ||
          `Failed to ${editing ? "update" : "add"} promoter`;
        toast.error(message);
      })

      .finally(() => {
        setLoading(false);
        setConfirmOpen(false);
      });
  };

  /** ------------ DELETE ------------ */
  const openDelete = (id) => {
    setDeleteId(id);
    setDeleteOpen(true);
  };

  const submitDelete = () => {
    setLoading(true);
    axiosInstance
      .delete(`/companies/${companyId}/promoters/${deleteId}`)
      .then(() => {
        toast.success("Promoter deleted");
        fetchPromoters();
      })
      .catch(() => toast.error("Failed to delete promoter"))
      .finally(() => {
        setLoading(false);
        setDeleteOpen(false);
        setDeleteId(null);
      });
  };

  /** ------------ GRID ------------ */
  const columns = useMemo(
    () => [
      {
        field: "imagePath",
        headerName: "Image",
        width: 100,
        renderCell: (params) => (
          <Avatar
            src={params.value}
            alt={params.row.name}
            variant="rounded"
            sx={{ width: 60, height: 60 }}
          />
        ),
      },
      { field: "name", headerName: "Name", flex: 1, minWidth: 150 },
      { field: "post", headerName: "Post", flex: 1, minWidth: 150 },
      {
        field: "actions",
        headerName: "Actions",
        width: 140,
        sortable: false,
        filterable: false,
        renderCell: ({ row }) => (
          <Box display="flex" gap={1}>
            <IconButton
              color="primary"
              disabled={loading}
              onClick={() => openAddEdit(row)}
            >
              <Edit />
            </IconButton>
            <IconButton
              color="error"
              disabled={loading}
              onClick={() => openDelete(row.id)}
            >
              <Delete />
            </IconButton>
          </Box>
        ),
      },
    ],
    [loading],
  );

  return (
    <Box m="20px">
      <Header title="COMPANY PROMOTERS" subtitle={`Company ID: ${companyId}`} />

      <Box display="flex" justifyContent="flex-end" mb={2}>
        <Button
          startIcon={loading ? <CircularProgress size={16} /> : <Add />}
          variant="contained"
          sx={{
            backgroundColor: colors.greenAccent[500],
            color: colors.grey[900],
          }}
          onClick={() => openAddEdit()}
          disabled={loading}
        >
          Add Promoter
        </Button>
      </Box>

      <Box
        height="70vh"
        sx={{
          "& .MuiDataGrid-root": { border: "none" },
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: colors.blueAccent[700],
            borderBottom: "none",
          },
          "& .MuiDataGrid-virtualScroller": {
            backgroundColor: colors.primary[400],
          },
          "& .MuiDataGrid-footerContainer": {
            borderTop: "none",
            backgroundColor: colors.blueAccent[700],
          },
        }}
      >
        <DataGrid
          rows={promoters}
          columns={columns}
          getRowId={(row) => row.id}
          disableRowSelectionOnClick
          pageSizeOptions={[5, 10, 20]}
          loading={loading}
          initialState={{
            pagination: { paginationModel: { pageSize: 10, page: 0 } },
          }}
        />
      </Box>

      {/* Add/Edit Dialog */}
      <Dialog
        open={addEditOpen}
        onClose={() => setAddEditOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>{editing ? "Edit Promoter" : "Add Promoter"}</DialogTitle>
        <DialogContent sx={{ display: "grid", gap: 2, mt: 1 }}>
          <TextField
            label="Name"
            name="name"
            fullWidth
            value={formData.name}
            onChange={handleFormChange}
            autoFocus
          />
          <TextField
            label="Post"
            name="post"
            fullWidth
            value={formData.post}
            onChange={handleFormChange}
          />
          <Box>
            <Typography variant="subtitle1">Image</Typography>
            <Box display="flex" gap={2} alignItems="center" mt={1}>
              {editing?.imagePath && !formData.imageFile && (
                <Avatar
                  src={editing.imagePath}
                  sx={{ width: 80, height: 80 }}
                  variant="rounded"
                />
              )}
              {formData.imageFile && (
                <Avatar
                  src={URL.createObjectURL(formData.imageFile)}
                  sx={{ width: 80, height: 80 }}
                  variant="rounded"
                />
              )}
              <Button variant="outlined" component="label">
                Upload
                <input
                  type="file"
                  hidden
                  accept="image/*"
                  onChange={(e) => handleFileChange(e.target.files[0])}
                />
              </Button>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setAddEditOpen(false)}
            disabled={loading}
            color="error"
            variant="outlined"
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmSave}
            disabled={loading}
            sx={{
              backgroundColor: colors.greenAccent[500],
              color: colors.grey[900],
            }}
          >
            {loading ? <CircularProgress size={18} /> : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add/Edit Confirm */}
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>Save this promoter?</DialogTitle>
        <DialogActions>
          <Button
            color="error"
            onClick={() => setConfirmOpen(false)}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            onClick={submitAddEdit}
            disabled={loading}
            sx={{
              backgroundColor: colors.greenAccent[500],
              color: colors.grey[900],
            }}
          >
            {loading ? <CircularProgress size={18} /> : "Yes, Save"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirm */}
      <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)}>
        <DialogTitle>Delete this promoter?</DialogTitle>
        <DialogActions>
          <Button
            color="error"
            onClick={() => setDeleteOpen(false)}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            onClick={submitDelete}
            disabled={loading}
            sx={{ color: colors.redAccent[500] }}
          >
            {loading ? <CircularProgress size={18} /> : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CompanyPromoters;
